import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Gauge,
  ShieldCheck,
  Wrench,
  Clock,
  Award,
  Phone,
  Mail,
  Instagram,
  Flag,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BrandName from "@/components/BrandName";
import { api, formatError } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { useLocalizedSettings } from "@/lib/localizedSettings";
import { useLocalizedService } from "@/lib/localizedService";
import { useT } from "@/lib/i18n";

const iconMap = {
  Ölwechsel: Gauge,
  Inspektion: ShieldCheck,
  Reparaturen: Wrench,
  Reifenservice: Wrench,
  Reifenwechsel: Wrench,
  "Bremsen-Service": ShieldCheck,
  Diagnose: Gauge,
  Fahrzeugpflege: Award,
  "TÜV/HU-Abnahme": Award,
};

export default function Landing() {
  const [services, setServices] = useState([]);
  const s = useLocalizedSettings();
  const localizeService = useLocalizedService();
  const t = useT();
  const audioRef = useRef(null);
  const playedRef = useRef(false);

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch((e) => console.warn(formatError(e)));
  }, []);

  useEffect(() => {
    if (!s.hero_sound_url) return;
    const audio = audioRef.current;
    if (!audio) return;

    // Start muted (allows autoplay in all browsers), then unmute right after
    audio.muted = true;
    audio.volume = 0.6;
    audio.currentTime = 0;

    const startPlayback = async () => {
      try {
        await audio.play();
        // Unmute immediately after playback started — most browsers keep playing
        audio.muted = false;
        playedRef.current = true;
      } catch (e) {
        // Autoplay blocked — fall back to first interaction
      }
    };

    startPlayback();

    // Fallback for stricter browsers: play on first user interaction
    const onInteract = () => {
      if (playedRef.current) return;
      audio.muted = false;
      audio.currentTime = 0;
      audio.play().then(() => {
        playedRef.current = true;
      }).catch(() => {});
    };
    window.addEventListener("pointerdown", onInteract, { passive: true, once: true });
    window.addEventListener("keydown", onInteract, { once: true });
    window.addEventListener("scroll", onInteract, { passive: true, once: true });
    window.addEventListener("touchstart", onInteract, { passive: true, once: true });

    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("scroll", onInteract);
      window.removeEventListener("touchstart", onInteract);
    };
  }, [s.hero_sound_url]);

  return (
    <div data-testid="landing-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/10 bg-[#09090B]">
        {/* Promo Sticker */}
        {s.promo_sticker_active && s.promo_sticker_headline && (
          <motion.div
            data-testid="promo-sticker"
            initial={{ scale: 0, rotate: -30, opacity: 0 }}
            animate={{ scale: 1, rotate: -12, opacity: 1 }}
            transition={{
              delay: 1.2,
              duration: 0.6,
              type: "spring",
              stiffness: 200,
              damping: 12,
            }}
            className="absolute left-8 top-1/2 z-10 hidden -translate-y-1/2 md:right-auto lg:left-auto lg:right-[38%] lg:top-[28%] lg:block"
          >
            <div className="relative flex h-36 w-36 flex-col items-center justify-center rounded-full bg-[#FF4B00] p-4 text-center shadow-2xl shadow-[#FF4B00]/40">
              <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/40" />
              <div className="text-lg font-black uppercase leading-tight text-white">
                {s.promo_sticker_headline}
              </div>
              {s.promo_sticker_subline && (
                <div className="mono mt-1 text-[9px] uppercase tracking-widest text-white/90">
                  {s.promo_sticker_subline}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {s.hero_sound_url && (
          <audio
            ref={audioRef}
            src={s.hero_sound_url}
            preload="auto"
            autoPlay
            playsInline
            data-testid="hero-audio"
          />
        )}

        {/* Animated car (slowly drives in from the right with subtle driving motion) */}
        {s.hero_car_url && (
          <div className="pointer-events-none absolute inset-x-0 top-[18%] z-0 flex justify-center overflow-hidden sm:top-[20%] lg:inset-x-auto lg:left-auto lg:right-6 lg:top-1/2 lg:-translate-y-1/2 lg:justify-end lg:overflow-visible">
            <motion.img
              data-testid="hero-car"
              src={s.hero_car_url}
              alt=""
              aria-hidden="true"
              initial={{ x: "120vw", opacity: 0, y: 0, rotate: 0 }}
              animate={{
                x: 0,
                opacity: [0, 0.6, 0.6, 0.55],
                y: [0, -3, 1, -2, 1, -2, 0, -1, 0],
                rotate: [0, -0.3, 0.2, -0.2, 0.15, -0.1, 0.1, 0, 0],
              }}
              transition={{
                x: { duration: 4, ease: [0.33, 0, 0.67, 1] },
                opacity: { duration: 4, times: [0, 0.15, 0.85, 1] },
                y: {
                  duration: 4,
                  times: [0, 0.12, 0.25, 0.38, 0.5, 0.62, 0.78, 0.9, 1],
                  ease: "linear",
                },
                rotate: {
                  duration: 4,
                  times: [0, 0.12, 0.25, 0.38, 0.5, 0.62, 0.78, 0.9, 1],
                  ease: "linear",
                },
              }}
              className="h-auto w-[115%] max-w-none select-none object-contain opacity-30 sm:w-[100%] sm:opacity-35 md:opacity-40 lg:h-[360px] lg:w-auto lg:opacity-100"
              style={{ transformOrigin: "50% 90%" }}
            />
          </div>
        )}
        {s.hero_car_url && (
          <div className="pointer-events-none absolute right-0 top-0 z-0 hidden h-full w-1/2 bg-gradient-to-r from-[#09090B] via-[#09090B]/40 to-transparent lg:block" />
        )}

        <div className="relative z-10 mx-auto max-w-7xl px-6 py-16 sm:py-24 lg:px-10 lg:py-40">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            {s.slogan && (
              <span data-testid="hero-slogan" className="tag-orange">
                {s.slogan}
              </span>
            )}
            <h1
              data-testid="hero-title"
              className="mt-8 leading-[0.95]"
            >
              <BrandName
                name={s.workshop_name}
                size="xl"
                stacked
                testId="hero-brand"
              />
            </h1>
            {s.hero_description && (
              <p className="mt-8 max-w-xl text-lg text-white/70">
                {s.hero_description}
              </p>
            )}

            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Link
                to="/buchen"
                data-testid="hero-cta-book"
                className="inline-flex items-center justify-center gap-3 bg-[#FF4B00] px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E64300]"
              >
                {t("hero.book")}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </Link>
              <a
                href="#services"
                data-testid="hero-cta-services"
                className="inline-flex items-center justify-center gap-3 border border-white/20 px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:border-white hover:bg-white/5"
              >
                {t("hero.services")}
              </a>
            </div>

            {/* Contact chips */}
            <div className="mt-12 flex flex-wrap gap-3">
              {s.phone && (
                <a
                  href={`tel:${s.phone.replace(/\s|\//g, "")}`}
                  className="mono inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white hover:border-[#FF4B00] hover:text-[#FF4B00]"
                >
                  <Phone className="h-3.5 w-3.5" strokeWidth={2} />
                  {s.phone}
                </a>
              )}
              {s.email && (
                <a
                  href={`mailto:${s.email}`}
                  className="mono inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white hover:border-[#FF4B00] hover:text-[#FF4B00]"
                >
                  <Mail className="h-3.5 w-3.5" strokeWidth={2} />
                  {s.email}
                </a>
              )}
              {s.instagram_handle && (
                <a
                  href={`https://instagram.com/${s.instagram_handle}`}
                  target="_blank"
                  rel="noreferrer"
                  className="mono inline-flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest text-white hover:border-[#FF4B00] hover:text-[#FF4B00]"
                >
                  <Instagram className="h-3.5 w-3.5" strokeWidth={2} />
                  {s.instagram_handle}
                </a>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section
        id="services"
        className="border-b border-white/10 px-6 py-24 lg:px-10 lg:py-32"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex items-end justify-between gap-6 border-b border-white/10 pb-8">
            <div>
              <span className="tag-orange">{t("services.tag")}</span>
              <h2 className="mt-6 text-4xl font-black uppercase sm:text-5xl">
                {t("services.heading")}
              </h2>
            </div>
            <p className="hidden max-w-sm text-sm text-white/60 md:block">
              {t("services.subtitle")}
            </p>
          </div>

          <div className="mt-12 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((rawSv, i) => {
              const sv = localizeService(rawSv);
              const Icon = iconMap[sv.name] || Wrench;
              return (
                <motion.div
                  key={sv.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid={`service-card-${sv.id}`}
                  className="group relative bg-[#0d0d10] p-8 transition-colors hover:bg-[#131318]"
                >
                  <div className="flex items-start justify-between">
                    <Icon
                      className="h-8 w-8 text-[#FF4B00]"
                      strokeWidth={1.5}
                    />
                    <div className="mono text-right text-[10px] uppercase tracking-[0.2em] text-white/40">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  </div>

                  <h3 className="mt-8 text-2xl font-black uppercase">
                    {sv.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-white/60">
                    {sv.description}
                  </p>

                  <div className="mt-8 flex items-end justify-between border-t border-white/10 pt-4">
                    <div>
                      <div className="mono text-[10px] uppercase tracking-[0.2em] text-white/40">
                        {t("services.from")}
                      </div>
                      <div className="mono mt-1 text-2xl font-bold text-white">
                        {sv.price_eur.toFixed(2).replace(".", ",")} €
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/50">
                      <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />
                      <span className="mono">{sv.duration_minutes} {t("services.min")}</span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-16 flex justify-start">
            <Link
              to="/buchen"
              data-testid="services-cta"
              className="inline-flex items-center gap-3 border-2 border-[#FF4B00] px-8 py-4 text-sm font-bold uppercase tracking-widest text-[#FF4B00] transition-colors hover:bg-[#FF4B00] hover:text-white"
            >
              {t("services.cta")}
              <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* TAGLINE BAND */}
      {s.tagline_bottom && (
        <section className="relative overflow-hidden border-b border-white/10 bg-[#0d0d10] px-6 py-14 lg:px-10">
          <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-6">
            <Flag
              className="h-10 w-10 flex-shrink-0 text-[#FF4B00]"
              strokeWidth={1.5}
            />
            <div
              data-testid="tagline-bottom"
              className="text-center text-2xl font-black uppercase tracking-wider text-white sm:text-3xl lg:text-4xl"
            >
              {s.tagline_bottom}
            </div>
            <Flag
              className="h-10 w-10 flex-shrink-0 text-[#FF4B00]"
              strokeWidth={1.5}
            />
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="border-b border-white/10 bg-[#09090B] px-6 py-24 lg:px-10">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
          <div>
            <h2 className="text-4xl font-black uppercase sm:text-5xl">
              {t("cta.ready")}
            </h2>
            <p className="mono mt-3 text-sm uppercase tracking-widest text-white/50">
              {t("cta.readySub")}
            </p>
          </div>
          <Link
            to="/buchen"
            data-testid="bottom-cta-book"
            className="inline-flex items-center gap-3 bg-[#FF4B00] px-10 py-5 text-sm font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E64300]"
          >
            {t("hero.book")}
            <ArrowRight className="h-5 w-5" strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
